/**
 * RequestTimeModal
 * Modal for users to request a custom time slot for a coaching session
 * Includes calendar, time selector, and message input
 */

import { XIcon } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import { Calendar } from "@/components/ui/calendar"
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog"
import { cn } from "@/lib"
import { useRequestCoachingTime } from "../hooks/useCoachingBooking"

interface RequestTimeModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  coachingSessionId: string
  sessionTitle: string
  expertName: string | null
}

// Time slot options for requests (24-hour format for backend)
const TIME_SLOTS = [
  "06:00",
  "07:00",
  "08:00",
  "09:00",
  "10:00",
  "11:00",
  "12:00",
  "13:00",
  "14:00",
  "15:00",
  "16:00",
  "17:00",
  "18:00",
  "19:00",
  "20:00",
  "21:00",
]

// Display format for time slots (with range like "9:00 AM - 10:00 AM")
const formatTimeDisplay = (time: string) => {
  const [hours, minutes] = time.split(":").map(Number)
  const period = hours >= 12 ? "PM" : "AM"
  const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours

  // Calculate end time (add 1 hour)
  const endHours = (hours + 1) % 24
  const endPeriod = endHours >= 12 ? "PM" : "AM"
  const displayEndHours = endHours > 12 ? endHours - 12 : endHours === 0 ? 12 : endHours

  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period} - ${displayEndHours}:${minutes.toString().padStart(2, "0")} ${endPeriod}`
}

export function RequestTimeModal({
  open,
  onOpenChange,
  coachingSessionId,
  sessionTitle,
  expertName,
}: RequestTimeModalProps) {
  const now = new Date()
  const [currentMonth, setCurrentMonth] = useState(now)
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined)
  const [selectedTime, setSelectedTime] = useState<string | undefined>(undefined)
  const [message, setMessage] = useState("")
  const requestTime = useRequestCoachingTime()

  // Get user's timezone
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  // Disable past dates
  const isDateDisabled = (date: Date) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return date < today
  }

  // Format date to YYYY-MM-DD for backend
  const formatDateForBackend = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  function formatSelectedDatesTimes(): string {
    if (!selectedDate && !selectedTime) return ""

    const parts: string[] = []
    if (selectedDate) {
      parts.push(
        `Date: ${selectedDate.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        })}`,
      )
    }
    if (selectedTime) {
      parts.push(`Time: ${formatTimeDisplay(selectedTime)}`)
    }
    return parts.join(" | ")
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (!selectedDate) {
      toast.error("Please select a date")
      return
    }

    if (!selectedTime) {
      toast.error("Please select a time")
      return
    }

    const preferredDates = [formatDateForBackend(selectedDate)]
    const preferredTimes = [selectedTime]

    requestTime.mutate(
      {
        coachingSessionId,
        preferredDates,
        preferredTimes,
        timezone: userTimezone,
        message: message.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success("Your time request has been sent to the coach")
          setSelectedDate(undefined)
          setSelectedTime(undefined)
          setMessage("")
          onOpenChange(false)
        },
        onError: (err: Error) => {
          toast.error(err.message || "Failed to send time request")
        },
      },
    )
  }

  function handleClose() {
    if (!requestTime.isPending) {
      setSelectedDate(undefined)
      setSelectedTime(undefined)
      setMessage("")
      onOpenChange(false)
    }
  }

  function handleMonthChange(date: Date) {
    setCurrentMonth(date)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="!max-w-[580px] overflow-y-auto max-h-[960px] border-connect-glass-border bg-[#1B1B1B] p-0 text-connect-cream sm:rounded-[30px]"
        overlayClass="bg-[rgba(2,2,2,0.9)] backdrop-blur-[25px]"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">Request a Custom Time</DialogTitle>
        <DialogDescription className="sr-only">
          Send a message to request a custom time slot for this coaching session
        </DialogDescription>

        <div className="flex flex-col p-8">
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h2 className="text-[24px] font-semibold text-connect-cream">Request a Time</h2>
              <p className="mt-1 text-[14px] text-connect-cream/60">
                Send a message to {expertName || "the coach"}
              </p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              disabled={requestTime.isPending}
              className="flex size-[44px] cursor-pointer items-center justify-center rounded-[10px] border border-connect-cream/20 bg-transparent transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <XIcon className="size-5 text-connect-cream" />
              <span className="sr-only">Close</span>
            </button>
          </div>

          {/* Session Info */}
          <div className="mb-5 rounded-xl border border-connect-cream/10 bg-connect-cream/5 p-3">
            <p className="text-[14px] font-medium text-connect-cream">{sessionTitle}</p>
          </div>

          {/* Calendar - Single date selection */}
          <div className="mb-5">
            <label className="mb-2 block text-[14px] font-medium text-connect-cream">
              Select Preferred Date
            </label>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              month={currentMonth}
              onMonthChange={handleMonthChange}
              disabled={isDateDisabled}
              className="w-full rounded-[15px]"
              classNames={{
                weekday:
                  "w-full flex items-center justify-center text-[12px] font-medium text-connect-cream/60 ",
                day: "h-11 w-full flex items-center justify-center rounded-lg text-sm transition-colors hover:bg-connect-cream/10",
                day_selected:
                  "bg-brand-terracotta text-my-rituality-bg hover:bg-brand-terracotta/90",
                day_disabled:
                  "text-connect-cream/20 hover:bg-transparent hover:text-connect-cream/20",
              }}
            />
            {selectedDate && (
              <p className="mt-2 text-[12px] text-connect-cream/60">
                Selected:{" "}
                {selectedDate.toLocaleDateString("en-US", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                })}
              </p>
            )}
          </div>

          {/* Time Slots - Single selection */}
          <div className="mb-5">
            <label className="mb-2 block text-[14px] font-medium text-connect-cream">
              Select Preferred Time
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {TIME_SLOTS.map(time => {
                const isSelected = selectedTime === time
                return (
                  <button
                    key={time}
                    type="button"
                    onClick={() => setSelectedTime(isSelected ? undefined : time)}
                    disabled={requestTime.isPending}
                    className={cn(
                      "h-[35px] px-[11px] py-[6px] rounded-[10px] text-[13px] font-medium transition-colors",
                      isSelected && "bg-brand-terracotta text-my-rituality-bg",
                      !isSelected &&
                        "border border-connect-cream text-connect-cream hover:bg-connect-cream/10",
                      "disabled:cursor-not-allowed disabled:opacity-50",
                    )}
                  >
                    {formatTimeDisplay(time)}
                  </button>
                )
              })}
            </div>
            {selectedTime && (
              <p className="mt-2 text-[12px] text-connect-cream/60">
                Selected: {formatTimeDisplay(selectedTime)}
              </p>
            )}
          </div>

          {/* Selected Dates/Times Display */}
          {formatSelectedDatesTimes() && (
            <div className="mb-5 rounded-xl border border-brand-terracotta/30 bg-brand-terracotta/10 p-3">
              <p className="text-[13px] text-connect-cream/80">
                <span className="font-medium">Requested:</span> {formatSelectedDatesTimes()}
              </p>
              <p className="mt-1 text-[11px] text-connect-cream/50">Timezone: {userTimezone}</p>
            </div>
          )}

          {/* Message Input */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label
                htmlFor="request-message"
                className="mb-2 block text-[14px] font-medium text-connect-cream"
              >
                Message to {expertName || "Coach"}{" "}
                <span className="text-connect-cream/50">(Optional)</span>
              </label>
              <textarea
                id="request-message"
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Add any additional details about your request..."
                maxLength={1000}
                rows={3}
                disabled={requestTime.isPending}
                className="w-full resize-none rounded-xl border border-connect-cream/20 bg-transparent px-4 py-3 text-[14px] text-connect-cream placeholder:text-connect-cream/30 focus:border-connect-cream/40 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              />
              <p className="mt-1 text-right text-[12px] text-connect-cream/40">
                {message.length} / 1000
              </p>
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-center pt-2">
              <button
                type="submit"
                disabled={requestTime.isPending || !selectedDate || !selectedTime}
                className="h-12.5 w-70 rounded-full text-[14px] font-semibold uppercase transition-colors bg-connect-cream text-my-rituality-bg hover:bg-connect-cream-hover disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {requestTime.isPending ? "Sending..." : "Send Request"}
              </button>
            </div>
          </form>

          {/* Helper Text */}
          <p className="mt-4 text-center text-[13px] text-connect-cream/50">
            The coach will receive your request and may confirm or suggest an alternative time.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
